import React, {useContext, useEffect, useState, useCallback} from 'react';
import {createPortal} from 'react-dom';
import {UserContext} from '../contexts/UserContext';
import api from '../services/api';
import queryClient from '../lib/queryClient';
import policy from '../constants/accountSafety.json';
import CalendarModalFrame from './modals/CalendarModalFrame';
import './AccountSafety.css';
import socket, {acquireSocketConnection,releaseSocketConnection} from '../../socket';
import {clearAllAuthData} from '../utils/auth';

const openSafety = detail => window.dispatchEvent(new CustomEvent('open-account-safety',{detail}));
const text = (key,lang) => policy.text[key]?.[lang] || policy.text[key]?.fr || key;
export function SafetyLink() {
  const {userInfo}=useContext(UserContext);
  return <button className="safety-button" onClick={()=>openSafety(null)}>{text('center',userInfo?.preferred_language)}</button>;
}
export function ContentSafetyButton({kind,contentID,authorID,revision='',variant='default'}) {
  const {userInfo}=useContext(UserContext);
  if(!authorID || Number(authorID)===Number(userInfo?.user_id))return null;
  const label=text('report',userInfo?.preferred_language);
  const isIcon=variant==='icon';
  return <button type="button" className={`safety-button${isIcon?' safety-button--icon':''}`} title={label} aria-label={label} onClick={event=>{
    event.stopPropagation();openSafety({kind,contentID,authorID,revision});
  }}>{isIcon ? <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
    <path d="M4 21V3m0 1c5-4 11 4 16 0v10c-5 4-11-4-16 0" />
  </svg> : '⚑'}</button>;
}
// A single boundary is used for every account type, including suspended members.
// Deletion/support remain accessible before accepting posting rules.
export function AccountSafetyBoundary({children}) {
  const {userInfo,setUserInfo}=useContext(UserContext);
  const [status,setStatus]=useState(null),[open,setOpen]=useState(false),[target,setTarget]=useState(null);
  const [deletionPreview,setDeletionPreview]=useState(null),[selectedCommissions,setSelectedCommissions]=useState([]),[commissionsReviewed,setCommissionsReviewed]=useState(false);
  const [queue,setQueue]=useState(null),[suspended,setSuspended]=useState([]);
  const [busy,setBusy]=useState(false),[error,setError]=useState(''),[password,setPassword]=useState(''),[confirm,setConfirm]=useState(false);
  const [reason,setReason]=useState('inappropriate'),[detail,setDetail]=useState(''),[sent,setSent]=useState(false);
  const lang=userInfo?.preferred_language || 'fr',t=key=>text(key,lang);
  const refresh=useCallback(async()=>{
    const latest=await api.get('/safety');setStatus(latest);
    if(latest.canModerate) {
      const [q,s]=await Promise.all([api.get('/safety/moderation'),api.get('/safety/moderation/suspended')]);setQueue(q);setSuspended(s.users);
    } else {setQueue(null);setSuspended([]);}
  },[]);
  useEffect(()=>{
    setStatus(null);setQueue(null);setOpen(false);setTarget(null);setError('');setPassword('');
    if(userInfo?.user_id)refresh().catch(()=>{});
  },[userInfo?.user_id,refresh]);
  useEffect(()=>{
    if(!userInfo?.user_id)return;
    const changed=async()=>{
      await queryClient.cancelQueries();
      queryClient.removeQueries();
      window.dispatchEvent(new Event('safety-changed'));
      await queryClient.invalidateQueries();
      await refresh().catch(()=>{});
    };
    acquireSocketConnection();socket.on('safetyChanged',changed);
    return ()=>{socket.off('safetyChanged',changed);releaseSocketConnection();};
  },[userInfo?.user_id,refresh]);
  useEffect(()=>{
    const listener=event=>{setOpen(true);setTarget(event.detail);setSent(false);setError('');setDetail('');};
    window.addEventListener('open-account-safety',listener);
    return ()=>window.removeEventListener('open-account-safety',listener);
  },[]);
  const run=async work=>{
    if(busy)return;setBusy(true);setError('');
    try{await work();}catch(e){setError(policy.text[e.data?.code] ? t(e.data.code):e.message || t('error'));}finally{setBusy(false);}
  };
  const invalidate=async()=>{
    await queryClient.cancelQueries();queryClient.removeQueries();
    window.dispatchEvent(new Event('safety-changed'));
    await refresh();setOpen(false);setTarget(null);
  };
  const accountState=status?.accountState || userInfo?.account_state;
  const restricted=accountState && accountState!=='active';
  const needsRules=status && !status.accepted;
  const format=value=>new Date(value).toLocaleDateString(lang);
  const logout=()=>run(async()=>{await api.post('/logout',{});clearAllAuthData();setUserInfo(null);queryClient.clear();});
  const rules=<section><h4>{t('rules')}</h4><p>{t('rulesBody')}</p>{needsRules && <button disabled={busy} onClick={()=>run(async()=>{
    await api.put('/safety/rules',{version:status.rulesVersion});await refresh();
  })}>{t('accept')}</button>}</section>;
  if(!userInfo)return <>{children}<DeletionReceiptNotice lang={lang}/></>;
  const content=target ? <>
    {sent?<p role="status">{t('sent')}</p>:<>
      <label>{t('report')}<select value={reason} onChange={e=>setReason(e.target.value)}>{policy.reasons.map(key=><option key={key} value={key}>{t(key)}</option>)}</select></label>
      <textarea aria-label={t('detail')} placeholder={t('detail')} maxLength={policy.maxReasonLength} value={detail} onChange={e=>setDetail(e.target.value)}/>
      <button disabled={busy} onClick={()=>run(async()=>{await api.post('/safety/reports',{...target,reason,detail});setSent(true);})}>{t('report')}</button>
    </>}
    <p>{t('blockHelp')}</p>
    <button disabled={busy} onClick={()=>run(async()=>{await api.put(`/safety/blocks/${target.authorID}`,target);await invalidate();})}>{t('block')}</button>
  </> : <>
    {rules}
    <section><h4>{t('blocks')}</h4>{status?.blocks.map(user=><div className="safety-row" key={user.user_id}><span>{user.user_name}</span><button disabled={busy} onClick={()=>run(async()=>{await api.delete(`/safety/blocks/${user.user_id}`);await invalidate();})}>{t('unblock')}</button></div>)}</section>
    <section><h4>{t('reports')}</h4>{status?.reports.map(report=><div className="safety-row" key={report.id}><span>{format(report.created_at)}</span><span>{t(report.state)}</span></div>)}</section>
    <section><h4>{t('delete')}</h4>
      {status?.deletion?<>
        <p>{t(status.deletion.state)} · {t('due')} {format(status.deletion.due_at)}</p>
        {status.deletion.state==='pending' && <button disabled={busy} onClick={()=>run(async()=>{await api.delete('/safety/deletion');sessionStorage.removeItem('account-deletion-receipt');await refresh();})}>{t('cancelRequest')}</button>}
      </>:<>
        <p>{t('deletionBody')}</p>
        <input type="password" autoComplete="current-password" aria-label={t('password')} placeholder={t('password')} value={password} onChange={e=>setPassword(e.target.value)}/>
        <label><input type="checkbox" checked={confirm} onChange={e=>setConfirm(e.target.checked)}/>{t('confirmDelete')}</label>
        <button disabled={busy||!confirm||!password||!status} onClick={()=>run(async()=>{
          const result=await api.post('/safety/deletion',{password,confirm});setPassword('');setConfirm(false);
          sessionStorage.setItem('account-deletion-receipt',result.receipt);await refresh();
        })}>{t('delete')}</button>
      </>}
    </section>
    {status?.canModerate && queue && <section><h4>{t('moderation')}</h4>
      {queue.reports.map(report=><article key={report.id}><b>{report.author_name}</b><p>{report.snapshot}</p><p>{t(report.reason)}</p>
        {['hide','dismiss','suspend'].map(action=><button key={action} disabled={busy} onClick={()=>run(async()=>{await api.post(`/safety/moderation/reports/${report.id}`,{action});await refresh();})}>{t(action)}</button>)}
      </article>)}
      <h4>{t('deletions')}</h4>{queue.deletions.map(deletion=><article key={deletion.id}><p>{deletion.user_login_id || t(deletion.state)} · {t('due')} {format(deletion.due_at)}</p>
        <button disabled={busy} onClick={()=>run(async()=>{const preview=await api.get(`/safety/moderation/deletions/${deletion.id}/preview`);setDeletionPreview({...preview,request:deletion});setSelectedCommissions([]);setCommissionsReviewed(false);})}>{t('review')}</button>
      </article>)}
      {deletionPreview && <article><h4>{t('deletionImpact')} · {deletionPreview.request.user_login_id}</h4>
        {Object.entries(deletionPreview.counts||{}).map(([key,count])=><p className="safety-row" key={key}><span>{t('impact_'+key)}</span><span>{count}</span></p>)}
        <h4>{t('commissionReview')}</h4><p>{t('commissionReviewHelp')}</p>
        {deletionPreview.commissions.map(c=><label key={c.id}><input type="checkbox" checked={selectedCommissions.includes(c.id)} onChange={e=>setSelectedCommissions(e.target.checked?[...selectedCommissions,c.id]:selectedCommissions.filter(id=>id!==c.id))}/>{c.artist_name} · {c.received_date}</label>)}
        <label><input type="checkbox" checked={commissionsReviewed} onChange={e=>setCommissionsReviewed(e.target.checked)}/>{t('commissionReviewed')}</label>
        <button disabled={busy||!commissionsReviewed} onClick={()=>{if(window.confirm(t('adminConfirm')+'\n'+(deletionPreview.request.user_login_id||deletionPreview.request.id)))run(async()=>{await api.post(`/safety/moderation/deletions/${deletionPreview.request.id}/complete`,{confirm:true,reviewedCommissions:commissionsReviewed,commissionIDs:selectedCommissions});setDeletionPreview(null);await refresh();});}}>{t('complete')}</button>
      </article>}
      <h4>{t('suspended')}</h4>{suspended.map(user=><div key={user.user_id} className="safety-row"><span>{user.user_name}</span><button disabled={busy} onClick={()=>run(async()=>{await api.post(`/safety/moderation/users/${user.user_id}/reinstate`,{});await refresh();})}>{t('reinstate')}</button></div>)}
    </section>}
  </>;
  return <>
    <DeletionReceiptNotice lang={lang}/>
    {status?.moderation && status.moderation.reports+status.moderation.deletions>0 && <button className="safety-button safety-reminder" onClick={()=>openSafety(null)}>
      {t('pendingModeration')} · {status.moderation.reports} / {status.moderation.deletions}
      {status.moderation.overdue>0 && <strong> · {t('overdue')}: {status.moderation.overdue}</strong>}
    </button>}
    {restricted||needsRules?<main className="safety-content safety-access"><h3>{t(restricted?'restricted':'rules')}</h3>{!restricted&&rules}<SafetyLink/><button onClick={logout} disabled={busy}>{t('logout')}</button></main>:children}
    {open&&createPortal(<CalendarModalFrame title={t(target?'report':'center')} onClose={()=>{if(!busy){setOpen(false);setPassword('');}}}>
      <div className="safety-content" aria-busy={busy}>{content}{error&&<p role="alert">{error}</p>}
        <a href="/app/#support" target="_blank" rel="noreferrer">gnegnegne.fr@gmail.com</a>
        <button disabled={busy} onClick={()=>run(refresh)}>{t('refresh')}</button>
      </div>
    </CalendarModalFrame>,document.body)}
  </>;
}

export function DeletionReceiptNotice({lang='fr'}) {
  const [complete,setComplete]=useState(false);
  useEffect(()=>{
    let active=true;
    const check=async()=>{
      const receipt=sessionStorage.getItem('account-deletion-receipt');
      if(!receipt)return;
      try {
        const result=await api.post('/safety/deletion-receipt',{receipt});
        if(active)setComplete(result.state==='completed');
      } catch(error) {
        if(error.status===404)sessionStorage.removeItem('account-deletion-receipt');
      }
    };
    check();window.addEventListener('focus',check);
    return ()=>{active=false;window.removeEventListener('focus',check);};
  },[]);
  if(!complete)return null;
  return <aside role="status" className="safety-content safety-receipt"><p>{text('completed',lang)}</p>
    <button onClick={()=>{sessionStorage.removeItem('account-deletion-receipt');setComplete(false);}}>{text('close',lang)}</button></aside>;
}
